/*
 * Dirt Masters — checkout button progress state + checkout_clicked event
 *
 * Fixes two things PostHog surfaced (2026-07-26):
 *
 * 1. $dead_click on "Check out". Submitting the cart form is a full-page POST
 *    that hands off to Shopify checkout. Dawn shows no feedback while it's in
 *    flight, so the page sits visibly unchanged for 2-3s — long enough for
 *    shoppers to re-tap (double POST) and for PostHog's dead-click detector to
 *    flag the click as dead. We apply Dawn's own `.loading` button state, which
 *    both tells the shopper it worked and mutates the DOM so the click no
 *    longer looks dead.
 *
 * 2. Missing checkout_started. That event comes from the Shopify Custom Pixel
 *    (docs/posthog-custom-pixel.js), which runs in Shopify's sandboxed pixel
 *    worker. That worker is not reliable — 116 sessions in one week logged
 *    "Failed to execute 'importScripts'" for it, and 2 of 5 converting sessions
 *    reached checkout_completed with no checkout_started at all, both mobile.
 *    checkout_clicked is captured here by posthog-js on our own page, so the
 *    top of the checkout funnel no longer depends on the pixel booting.
 *
 * Covers every theme-rendered checkout submit ([name="checkout"]): cart page,
 * cart drawer, cart notification. Shopify's dynamic checkout buttons (Shop Pay
 * et al.) render in their own frames and are out of reach — those shoppers are
 * still covered by the pixel alone.
 */
(function () {
  const CHECKOUT_SELECTOR = '[name="checkout"]';
  const LOADING_CLASS = 'dm-checkout-loading';

  // If the POST never lands (offline, blocked, back-button restore that skips
  // bfcache), don't leave the shopper with a permanently dead-looking button.
  const RESET_MS = 15000;

  function liveCartCount() {
    const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (!bubble) return null;
    const count = parseInt(bubble.textContent.trim(), 10);
    return isNaN(count) ? null : count;
  }

  function setLoading(button) {
    button.classList.add('loading', LOADING_CLASS);
    button.setAttribute('aria-busy', 'true');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  function clearLoading(button) {
    button.classList.remove('loading', LOADING_CLASS);
    button.removeAttribute('aria-busy');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function captureCheckoutClicked(button) {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;

    const properties = { source: button.dataset.dmCheckoutSource || 'unknown' };

    const count = liveCartCount();
    if (count !== null) properties.item_count = count;

    // Only rendered where the surrounding section re-renders on cart change, so
    // the number is never stale (cart page + drawer; not the cart notification).
    const cents = parseInt(button.dataset.dmCartTotal, 10);
    if (!isNaN(cents)) {
      properties.value = cents / 100;
      if (button.dataset.dmCartCurrency) properties.currency = button.dataset.dmCartCurrency;
    }

    try {
      // sendBeacon so the event is on the wire before the checkout navigation,
      // rather than riding on posthog-js's pagehide flush.
      window.posthog.capture('checkout_clicked', properties, { transport: 'sendBeacon' });
    } catch (error) {
      // Never let analytics stop a checkout.
    }
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const button = target.closest(CHECKOUT_SELECTOR);
    if (!button || button.disabled) return;

    // Already submitting: swallow the re-tap rather than POST the cart twice.
    // (The CSS also drops pointer events, so this is belt-and-braces.)
    if (button.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    setLoading(button);
    captureCheckoutClicked(button);
    window.setTimeout(function () {
      clearLoading(button);
    }, RESET_MS);
  });

  // Back-button/bfcache restore hands back the page mid-submit — reset the button.
  window.addEventListener('pageshow', function () {
    document.querySelectorAll(CHECKOUT_SELECTOR + '.' + LOADING_CLASS).forEach(clearLoading);
  });
})();
